/**
 * Storefront GraphQL Client (browser)
 *
 * Purpose:
 * - Provide a simple, resilient wrapper around Shopify's Storefront GraphQL API
 * - Centralize configuration, debugging, and retry logic
 *
 * Usage:
 * - Theme injects window.__SF_API__ with { domain, token, version, country, language, debug }
 * - Call: window.StorefrontClient.request(query, variables?, options?)
 *   - options: { signal?, retries?, retryDelayMs? }
 * - Example:
 *   const { data, errors } = await window.StorefrontClient.request(`query{ products(first:1){edges{node{handle}}}}`);
 *
 * Debugging:
 * - Enable theme setting "Enable GraphQL debug logging" to print verbose logs
 * - All console logs are prefixed with [SF-GraphQL]
 */
(function storefrontGraphQLClient() {
  'use strict';

  /** Returns a safe, immutable snapshot of the global config. */
  function getConfig() {
    const g = (typeof window !== 'undefined' && window.__SF_API__) || {};
    return {
      domain: typeof g.domain === 'string' && g.domain ? g.domain : (typeof window !== 'undefined' ? window.location.origin : ''),
      token: typeof g.token === 'string' ? g.token : '',
      version: typeof g.version === 'string' && g.version ? g.version : '2024-07',
      country: typeof g.country === 'string' && g.country ? g.country : 'US',
      language: typeof g.language === 'string' && g.language ? g.language : 'en',
      debug: Boolean(g.debug)
    };
  }

  /** Conditional logger gated by the theme "graphql_debug" setting. */
  function debugLog(...args) {
    try {
      const cfg = getConfig();
      if (cfg.debug && typeof console !== 'undefined' && console.debug) {
        console.debug('[SF-GraphQL]', ...args);
      }
    } catch (_) {
      /* no-op */
    }
  }

  function warnLog(...args) {
    try {
      if (typeof console !== 'undefined' && console.warn) {
        console.warn('[SF-GraphQL]', ...args);
      }
    } catch (_) {
      /* no-op */
    }
  }

  /** Simple sleep helper for retry backoff. */
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Perform a Storefront GraphQL request with basic retries.
   * - Retries on 429 and 5xx responses with exponential backoff.
   * - Propagates fetch AbortError if the provided signal aborts.
   */
  async function request(query, variables = {}, options = {}) {
    const cfg = getConfig();
    const { signal, retries = 2, retryDelayMs = 400 } = options;

    if (!cfg.token) {
      throw new Error('Missing Storefront API token. Provide it in theme settings → Search (GraphQL).');
    }

    // Construct endpoint from domain and version. Example: https://example.com/api/2024-07/graphql.json
    const endpoint = `${cfg.domain}/api/${cfg.version}/graphql.json`;

    // Compose the GraphQL body with in-context variables if used inside queries.
    const body = JSON.stringify({ query, variables });

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-Shopify-Storefront-Access-Token': cfg.token
    };

    let attempt = 0;
    // Last caught error to rethrow if all retries fail.
    let lastError = null;

    while (attempt <= retries) {
      try {
        if (attempt > 0) {
          // Exponential backoff: base delay * 2^(attempt-1)
          const delay = retryDelayMs * Math.pow(2, attempt - 1);
          await sleep(delay);
        }

        debugLog('Request', { endpoint, attempt, variables, query: query?.slice?.(0, 120) + '…' });

        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body,
          signal
        });

        const isRateLimited = response.status === 429;
        const isServerError = response.status >= 500;

        // Attempt to parse JSON even on non-2xx to forward error payloads
        const json = await response.json().catch(() => ({}));

        if (!response.ok) {
          debugLog('HTTP error', { status: response.status, json });
          if ((isRateLimited || isServerError) && attempt < retries) {
            attempt += 1;
            continue;
          }
          const httpError = new Error(`Storefront GraphQL HTTP ${response.status}`);
          httpError.response = response;
          httpError.payload = json;
          throw httpError;
        }

        if (json.errors) {
          // GraphQL errors are returned with 200 but contain an errors array
          warnLog('GraphQL errors', json.errors);
        }

        debugLog('Response', json);
        return json; // { data, errors, extensions }
      } catch (error) {
        // If the error is an abort, surface immediately
        if (error?.name === 'AbortError') {
          debugLog('Aborted');
          throw error;
        }

        lastError = error;
        warnLog('Request failure', { attempt, error });

        // Only retry network/HTTP categories; if maxed out, break
        if (attempt >= retries) {
          break;
        }
        attempt += 1;
      }
    }

    throw lastError || new Error('Unknown Storefront GraphQL error');
  }

  /**
   * Lightweight self-test to validate token/endpoint.
   * Queries a single product edge to ensure the API is reachable.
   */
  async function test() {
    const smokeQuery = /* GraphQL */ `
      query TestProducts($first: Int!) {
        products(first: $first) {
          edges { node { id handle } }
        }
      }
    `;
    return request(smokeQuery, { first: 1 });
  }

  // Expose client globally in a non-destructive way.
  if (typeof window !== 'undefined') {
    window.StorefrontClient = Object.freeze({
      request,
      test,
      get config() {
        return getConfig();
      }
    });
    debugLog('Client initialized', getConfig());
  }
})();

